import { ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, ErrorState, InlineLoading } from "@marekl/adminer/components";
import type { DashboardAlert } from "../../../lib/queries/dashboard";
import { alertContent, sortAlerts } from "./alertCopy";
import styles from "./DashboardPage.module.css";

// „Co je třeba udělat" — the dashboard's second block, and the one people
// come back to. Rows are composed here rather than from the lib `Alert`
// because Alert wants a density and a trailing action slot it does not have (the accent edge, which used to be on this list too, is switchable since 0.32.0 and tokens.css turns it off)
// (docs/adminer-redesign.md §4), and DESIGN.md §4 forbids one.

const ROW_CLASS: Record<DashboardAlert["severity"], string> = {
  info: styles.rowInfo,
  warn: styles.rowWarn,
  err: styles.rowErr,
};

export interface AttentionListProps {
  alerts: DashboardAlert[] | undefined;
  isLoading?: boolean;
  error?: unknown;
}

export function AttentionList({ alerts, isLoading, error }: AttentionListProps) {
  return (
    <Card>
      <Card.Header title="Co je třeba udělat" />
      <Card.Content>
        {isLoading ? (
          <InlineLoading>Načítám upozornění…</InlineLoading>
        ) : error ? (
          <ErrorState error={error} title="Nepodařilo se načíst upozornění." />
        ) : !alerts || alerts.length === 0 ? (
          <div className={styles.allClear}>
            <CircleCheck size={16} aria-hidden />
            <span>Vše je v pořádku, nic nečeká na vyřízení.</span>
          </div>
        ) : (
          <div className={styles.attention}>
            {sortAlerts(alerts).map((a, i) => {
              const c = alertContent(a);
              const Icon = c.icon;
              return (
                <div key={`${a.kind}-${i}`} className={`${styles.row} ${ROW_CLASS[a.severity]}`}>
                  <span className={styles.rowIcon}>
                    <Icon size={18} aria-hidden />
                  </span>
                  <div className={styles.rowText}>
                    <span className={styles.rowTitle}>{c.title}</span>
                    {c.body ? ` · ${c.body}` : ""}
                    {c.note && <small className={styles.rowNote}>{c.note}</small>}
                  </div>
                  {c.cta && (
                    <Link to={c.cta.to} className={styles.action}>
                      {c.cta.label}
                      <ArrowRight size={14} aria-hidden />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
